import brandAssets from '@streamlain/brand/brand-assets.json';
import { css, html, LitElement, svg } from 'lit';
import { property } from 'lit/decorators.js';

import type { ChatStatus } from '../ai-chat-messages/type';

/** What the Mate is doing, shown by its eyes and a ring (docs/mate-mark.md in sl-website2). */
export type MateState = 'idle' | 'think' | 'work' | 'wait';

/** Stops of a body gradient: offset 0–1 and colour. */
export type MateGradient = ReadonlyArray<readonly [number, string]>;

/** One grid of the mark: body and ring radius, ring stroke, eyes as [x, y, width, height, radius]. */
interface MateGrid {
  view: number;
  body: number;
  ring: number;
  stroke: number;
  eyes: number[][];
}

/** Geometry and own gradient from the brand assets, shared with the Telegram bot picture. */
const MATE_MARK = brandAssets.mateMark as {
  gradient: [number, string][];
  grid48: MateGrid;
  grid16: MateGrid;
};

/** Colour name the server stores per Mate (mates.mark_color). */
export type MateMarkColor =
  | 'own'
  | 'mint'
  | 'lavender'
  | 'sky'
  | 'amber'
  | 'rose';

/**
 * Body gradients by colour: the own Mate brand blue to lavender, further Mates the palette of
 * docs/mate-mark.md in sl-website2. The only place these colours live in the frontend.
 */
export const MATE_GRADIENTS: Record<MateMarkColor, MateGradient> = {
  own: MATE_MARK.gradient,
  mint: [
    [0, '#7fd6b0'],
    [1, '#3fae86'],
  ],
  lavender: [
    [0, '#b8a8ff'],
    [1, '#7d6be0'],
  ],
  sky: [
    [0, '#a9d7ff'],
    [1, '#5aa2e0'],
  ],
  amber: [
    [0, '#f6cf7a'],
    [1, '#d99a2b'],
  ],
  rose: [
    [0, '#f4a7b9'],
    [1, '#d0607e'],
  ],
};

/**
 * The state of the mark for a chat: a confirmation waiting for „Erlauben" wins, then
 * reasoning before the first token, then tools and streaming.
 */
export function mateStateOf(
  status: ChatStatus,
  awaitingConfirmation = false
): MateState {
  if (awaitingConfirmation) return 'wait';
  if (status === 'loading') return 'think';
  if (status === 'transmitting') return 'work';
  return 'idle';
}

/** Below 20 px the 16 grid with pixel-tuned eyes is drawn, from 20 px the 48 grid. */
const GRID_16 = MATE_MARK.grid16;
const GRID_48 = MATE_MARK.grid48;

let nextGradientId = 0;

export class MateMark extends LitElement {
  static override styles = css`
    :host {
      display: inline-flex;
      flex: none;
    }
    svg {
      overflow: visible;
    }
    .mate-eyes,
    .mate-eye,
    .mate-body,
    .mate-ring {
      transform-box: fill-box;
      transform-origin: center;
    }
    .mate-ring {
      fill: none;
      opacity: 0;
    }
    :host([data-state='work']) .mate-eyes {
      transform: translate(18%, 4%);
    }
    :host([data-state='work']) .mate-ring {
      opacity: 1;
      stroke: #a9d7ff;
      stroke-dasharray: 40 260;
    }
    :host([data-state='wait']) .mate-eyes {
      transform: translateY(-10%);
    }
    :host([data-state='wait']) .mate-ring {
      opacity: 1;
      stroke: #f2c14e;
    }
    /* A resting Mate stands still (the chat title, a finished answer): it moves only while it works. */
    @media (prefers-reduced-motion: no-preference) {
      :host([data-state='think']) .mate-eyes {
        animation: mate-scan 1.6s ease-in-out infinite;
      }
      :host([data-state='think']) .mate-body {
        animation: mate-breathe 1.6s ease-in-out infinite;
      }
      :host([data-state='work']) .mate-ring {
        animation: mate-turn 1.4s linear infinite;
      }
      :host([data-state='wait']) .mate-eye {
        animation: mate-blink 2.4s infinite;
      }
      :host([data-state='wait']) .mate-ring {
        animation: mate-pulse 1.8s ease-in-out infinite;
      }
    }
    @media (prefers-reduced-motion: reduce) {
      :host([data-state='work']) .mate-eyes,
      :host([data-state='wait']) .mate-eyes {
        transform: none;
      }
    }
    @keyframes mate-blink {
      0%,
      92%,
      100% {
        transform: scaleY(1);
      }
      95% {
        transform: scaleY(0.12);
      }
    }
    @keyframes mate-scan {
      0%,
      100% {
        transform: translateX(-12%);
      }
      50% {
        transform: translateX(12%);
      }
    }
    @keyframes mate-breathe {
      0%,
      100% {
        transform: scale(1);
      }
      50% {
        transform: scale(0.94);
      }
    }
    @keyframes mate-turn {
      to {
        transform: rotate(360deg);
      }
    }
    @keyframes mate-pulse {
      0%,
      100% {
        opacity: 0.35;
      }
      50% {
        opacity: 1;
      }
    }
  `;

  /** Edge length in px: 16 notices and lists, 20 answer head and Mate choice, 52 island. */
  @property({ type: Number })
  accessor size = 20;

  @property({ attribute: 'data-state', reflect: true })
  accessor state: MateState = 'idle';

  /** The Mate's colour; its gradient runs from top left to bottom right. */
  @property({ attribute: false })
  accessor markColor: MateMarkColor = 'own';

  private readonly gradientId = `mate-gradient-${nextGradientId++}`;

  override render() {
    const grid = this.size < 20 ? GRID_16 : GRID_48;
    const centre = grid.view / 2;
    return html`<svg
      width=${this.size}
      height=${this.size}
      viewBox="0 0 ${grid.view} ${grid.view}"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id=${this.gradientId} x1="0" y1="0" x2="1" y2="1">
          ${MATE_GRADIENTS[this.markColor].map(
            ([offset, colour]) =>
              svg`<stop offset=${offset} stop-color=${colour}></stop>`
          )}
        </linearGradient>
      </defs>
      <circle
        class="mate-ring"
        cx=${centre}
        cy=${centre}
        r=${grid.ring}
        stroke-width=${grid.stroke}
        pathLength="300"
      ></circle>
      <circle
        class="mate-body"
        cx=${centre}
        cy=${centre}
        r=${grid.body}
        fill="url(#${this.gradientId})"
      ></circle>
      <g class="mate-eyes" fill="#fff">
        ${grid.eyes.map(
          ([x, y, width, height, radius]) =>
            svg`<rect class="mate-eye" x=${x} y=${y} width=${width} height=${height} rx=${radius}></rect>`
        )}
      </g>
    </svg>`;
  }
}

/** Registers <mate-mark> once: from the AI effects and from the AI island, whichever comes first. */
export function defineMateMark() {
  if (!customElements.get('mate-mark')) {
    customElements.define('mate-mark', MateMark);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mate-mark': MateMark;
  }
}
